/**
 * Event registry — THE analytics contract (ADR-003; PRD v1.3 Appendix A).
 *
 * SINGLE-WRITER RULE (sprint-01): only Stream A edits this file. Streams
 * B/C/D consume it read-only. Adding an event = a PR that adds one EventMap
 * entry + one EVENT_DEFS entry, reviewed against PRD Appendix A.
 *
 * PHI RULE: props are IDs, enums, counts, and booleans ONLY. Never names,
 * phones, emails, UHIDs, addresses, or note/document contents. Prop names
 * matching the denylist in track.ts (/(name|phone|mobile|email|address|uhid|
 * dob|note|remarklabel)/i) are dropped at runtime even if listed here — name
 * props to pass the denylist (`remark_id` ✓, `remark_label` ✗).
 */

/** PRD's 12 feature areas + `reliability` (documented extension for A.15 signals). */
export type FeatureArea =
  | "auth"
  | "leads"
  | "patient360"
  | "calling"
  | "booking"
  | "tasks"
  | "overview"
  | "dashboards"
  | "exports"
  | "notifications"
  | "broadcasts"
  | "config"
  | "reliability";

export type EventPriority = "P0" | "P1" | "P2";

export type BookingType = "doctor" | "diagnostic";
export type PaymentMode = "upi" | "pay_at_hospital";
export type ConnectivityStatus =
  "RNR" | "DND" | "CALL_BACK_LATER" | "TRUE_CONNECT";
export type LeadOpenedFrom = "tab" | "search" | "overview" | "deeplink";
/** v1 is frontend-only: every entry event is manual_ui. Phase 2 adds `system` server-side (ADR-004). */
export type CreatedVia = "manual_ui";

/** Wave 2 enums (PRD Appendix A.8 / A.12 / A.14 / A.17). */
export type ContactChannel = "call" | "whatsapp" | "email";
export type OverviewTab = "opportunities" | "patients" | "tasks";
export type DashboardType =
  "frontline" | "team_lead" | "facility_admin" | "tenant_admin";
export type ConfigEntity =
  | "user"
  | "team"
  | "permission_group"
  | "routing_group"
  | "disposition_config"
  | "pipeline_stage"
  | "opportunity_type"
  | "doctor"
  | "service"
  | "facility"
  | "department"
  | "outbound_connectivity"
  | "google_ads"
  | "meta_integration"
  | "web_form";
export type ConfigAction =
  "create" | "update" | "activate" | "deactivate" | "delete" | "clone";

/**
 * One entry per event: the exact property shape a call site may pass.
 * Global props (tenant_id, tenant_tier, facility_id, environment, role,
 * app_version) are stamped by identity.ts/track.ts — never listed here.
 */
export interface EventMap {
  // ── A.10 Auth & session ────────────────────────────────────────────────
  login_succeeded: { used_2fa?: boolean; device_known?: boolean };
  login_failed: { reason: string; attempt_number?: number };
  session_refresh_failed: { reason?: string; session_age_ms?: number };

  // ── A.6/A.7 Lead intake & working ──────────────────────────────────────
  lead_created: {
    created_via: CreatedVia;
    lead_source: string;
    source_channel?: string;
    opportunity_type?: string;
    is_dedupe_match?: boolean;
  };
  lead_create_started: { step_index: number };
  lead_create_details_entered: { step_index: number };
  lead_create_abandoned: {
    last_step: string;
    step_index: number;
    time_in_flow_ms: number;
  };
  lead_search_performed: {
    search_type: string;
    result_count?: number;
    action_taken: "opened" | "linked" | "created_new" | "none";
  };
  lead_opened: {
    opportunity_id: string;
    from: LeadOpenedFrom;
    position?: number;
  };

  // ── A.11 Opportunity lifecycle / conversion ────────────────────────────
  opportunity_created: {
    created_via: CreatedVia;
    opportunity_type: string;
    department_id?: string;
    lead_source?: string;
  };
  opportunity_stage_changed: {
    opportunity_id?: string;
    opportunity_type: string;
    from_stage?: string;
    to_stage: string;
  };
  opportunity_won: {
    opportunity_id?: string;
    opportunity_type: string;
    days_to_close?: number;
  };
  opportunity_lost: {
    opportunity_id?: string;
    opportunity_type: string;
    loss_reason?: string;
    days_to_close?: number;
  };

  // ── A.4 Calling & disposition (call-scoped step funnel; flow_id correlates
  // the session — opportunity_id attaches at submit, per opportunity) ────────
  call_started: { opportunity_id?: string; direction: "outbound" | "inbound" };
  call_connected: {
    opportunity_id: string;
    duration_sec?: number;
    attempt_number?: number;
  };
  disposition_form_opened: { opportunity_id?: string; step_index: number };
  disposition_connectivity_selected: {
    opportunity_id?: string;
    step_index: number;
    connectivity_status: ConnectivityStatus;
  };
  disposition_remark_selected: {
    opportunity_id?: string;
    step_index: number;
    remark_id: string;
  };
  followup_scheduled: { opportunity_id?: string; lead_time_hrs?: number };
  lead_disposition_submitted: {
    opportunity_id: string;
    connectivity_status: ConnectivityStatus;
    previous_status?: string;
    new_status: string;
    new_stage?: string;
    has_followup: boolean;
  };
  disposition_abandoned: {
    opportunity_id?: string;
    last_step: string;
    step_index: number;
    time_in_flow_ms: number;
  };

  // ── A.5 Patient-level disposition ──────────────────────────────────────
  patient_rnr_submitted: {
    attempt_number?: number;
    auto_converted_to_dnd?: boolean;
    open_opps_affected?: number;
  };
  patient_dnd_flagged: { flagged_opps?: number };
  patient_cbl_submitted: {
    tasks_created?: number;
    open_opps_affected?: number;
  };

  // ── A.1/A.2 Booking wizards (step funnel; flow_id correlates the session —
  // the real booking_id exists only from the slot-step book API onward, so it
  // is optional on the non-terminal steps and REQUIRED on appointment_booked) ─
  booking_started: {
    booking_id?: string;
    booking_type: BookingType;
    opportunity_id?: string;
  };
  booking_opportunity_selected: {
    booking_id?: string;
    booking_type: BookingType;
    step_index: number;
    opportunity_type?: string;
    is_new_opportunity?: boolean;
  };
  booking_doctor_selected: {
    booking_id?: string;
    booking_type: BookingType;
    step_index: number;
    doctor_id: string;
    speciality_id?: string;
    service_id?: string;
  };
  booking_tests_selected: {
    booking_id?: string;
    booking_type: BookingType;
    step_index: number;
    department_id: string;
    test_count: number;
    diagnostic_type?: string;
  };
  booking_slot_selected: {
    booking_id?: string;
    booking_type: BookingType;
    step_index: number;
    appointment_date: string;
  };
  booking_details_reviewed: {
    booking_id?: string;
    booking_type: BookingType;
    step_index: number;
    amount?: number;
    currency?: string;
  };
  booking_payment_initiated: {
    booking_id?: string;
    booking_type: BookingType;
    step_index: number;
    payment_mode: PaymentMode;
  };
  appointment_booked: {
    booking_id: string;
    booking_type: BookingType;
    step_index?: number;
    opportunity_id?: string;
    doctor_id?: string;
    department_id?: string;
    amount?: number;
    payment_mode?: PaymentMode;
    test_count?: number;
  };
  booking_abandoned: {
    booking_id?: string;
    booking_type?: BookingType;
    last_step: string;
    step_index: number;
    time_in_flow_ms: number;
  };

  // ── A.3 Post-booking changes ───────────────────────────────────────────
  appointment_rescheduled: {
    booking_type: BookingType;
    reason?: string;
    days_before_appt?: number;
  };
  appointment_cancelled: {
    booking_type: BookingType;
    reason?: string;
    days_before_appt?: number;
  };

  // ── Wave 2 · A.7 Lead working — My Leads ────────────────────────────────
  // PRD says `filter_name`; renamed `filter_key` — "name" trips the PHI
  // denylist and the event would self-drop (the remark_label lesson).
  leads_tab_switched: { tab: string; result_count?: number };
  leads_filter_applied: {
    filter_key: string;
    value_count?: number;
    active_filter_count?: number;
  };

  // ── Wave 2 · A.8 Patient 360 ────────────────────────────────────────────
  patient_360_opened: {
    entry_point?: string;
    opportunity_count?: number;
    open_task_count?: number;
  };
  patient_360_tab_viewed: {
    tab: "activities" | "opportunities" | "tasks" | "orders" | "documents";
  };
  note_added: { has_attachment?: boolean };
  rx_viewed: { opportunity_id: string };
  contact_channel_clicked: { channel: ContactChannel };

  // ── Wave 2 · US-D2 Patient-registration funnel ──────────────────────────
  patient_registration_started: { step_index: number };
  patient_registration_submitted: { step_index?: number };
  patient_registration_abandoned: {
    last_step: string;
    step_index: number;
    time_in_flow_ms: number;
  };

  // ── Wave 2 · A.9 Tasks (SPOC / agent) ───────────────────────────────────
  task_opened: {
    task_source?: string;
    task_type?: string;
    priority?: string;
    is_unread?: boolean;
    sla_breached?: boolean;
  };
  task_completed: {
    task_source?: string;
    task_type?: string;
    time_to_complete_ms?: number;
    disposition?: string;
  };
  task_disposition_set: {
    department_id?: string;
    disposition?: string;
    phase?: string;
  };
  task_reopened: { task_source?: string; reopen_count?: number };
  task_created: {
    task_type?: string;
    has_due_date?: boolean;
    linked_to_opportunity?: boolean;
  };

  // ── Wave 2 · A.12 Overview workspace (+ bulk_update funnel) ─────────────
  overview_tab_viewed: { tab: OverviewTab; result_count?: number };
  overview_filter_applied: {
    tab: OverviewTab;
    filter_key: string;
    value_count?: number;
    active_filter_count?: number;
  };
  overview_record_opened: { tab: OverviewTab; entity_type?: string };
  bulk_update_started: {
    entity: string;
    selection_count?: number;
    used_select_all?: boolean;
    step_index?: number;
  };
  bulk_update_applied: {
    entity: string;
    selection_count?: number;
    fields_changed?: number;
    affected_count?: number;
    result?: string;
    step_index?: number;
  };
  bulk_update_abandoned: {
    entity?: string;
    last_step: string;
    step_index: number;
    time_in_flow_ms: number;
  };

  // ── Wave 2 · A.13 Data exports (funnel; export_completed lands via poll) ─
  export_started: {
    entity: "opportunities" | "patients" | "tasks";
    active_filter_count?: number;
    step_index?: number;
  };
  export_submitted: {
    entity: "opportunities" | "patients" | "tasks";
    export_id?: string;
    step_index?: number;
  };
  export_completed: {
    entity: "opportunities" | "patients" | "tasks";
    export_id?: string;
    status?: string;
  };
  export_abandoned: {
    entity?: "opportunities" | "patients" | "tasks";
    last_step: string;
    step_index: number;
    time_in_flow_ms: number;
  };

  // ── Wave 2 · A.14 Dashboards ────────────────────────────────────────────
  dashboard_viewed: { dashboard_type: DashboardType; date_preset?: string };
  agent_dashboard_viewed: {
    period_tab?: "this_month" | "last_month" | "this_year";
  };

  // ── Wave 2 · A.15 Notifications ─────────────────────────────────────────
  notification_center_opened: { unread_count?: number };
  notification_clicked: {
    entity_type?: string;
    priority?: string;
    time_to_click_ms?: number;
  };

  // ── Wave 2 · A.16 Broadcasts ────────────────────────────────────────────
  broadcast_sent: {
    priority?: string;
    target_type?: "all" | "specific_users";
    recipient_count?: number;
    scheduled?: boolean;
  };

  // ── Wave 2 · A.17 Configuration & administration ────────────────────────
  config_entity_changed: { entity: ConfigEntity; action: ConfigAction };
  web_form_published: { field_count?: number; facility_bound?: boolean };

  // ── A.15 Reliability signals (rate-limited by track.ts) ────────────────
  api_error_encountered: {
    endpoint_group: string;
    status_code: number;
    method: string;
  };
  socket_disconnected: {
    reason?: string;
    reconnect_attempts?: number;
    session_age_ms?: number;
  };
}

export type EventName = keyof EventMap;

interface EventDef<E extends EventName> {
  featureArea: FeatureArea;
  priority: EventPriority;
  /** Exhaustive allow-list — track.ts drops any prop key not listed here. */
  allowedProps: ReadonlyArray<Extract<keyof EventMap[E], string>>;
}

/** Mapped type ⇒ allowedProps entries MUST be real keys of the event's props. */
export const EVENT_DEFS: { [E in EventName]: EventDef<E> } = {
  // Auth
  login_succeeded: {
    featureArea: "auth",
    priority: "P0",
    allowedProps: ["used_2fa", "device_known"],
  },
  login_failed: {
    featureArea: "auth",
    priority: "P1",
    allowedProps: ["reason", "attempt_number"],
  },
  session_refresh_failed: {
    featureArea: "auth",
    priority: "P1",
    allowedProps: ["reason", "session_age_ms"],
  },

  // Lead intake & working
  lead_created: {
    featureArea: "leads",
    priority: "P0",
    allowedProps: [
      "created_via",
      "lead_source",
      "source_channel",
      "opportunity_type",
      "is_dedupe_match",
    ],
  },
  lead_create_started: {
    featureArea: "leads",
    priority: "P1",
    allowedProps: ["step_index"],
  },
  lead_create_details_entered: {
    featureArea: "leads",
    priority: "P1",
    allowedProps: ["step_index"],
  },
  lead_create_abandoned: {
    featureArea: "leads",
    priority: "P1",
    allowedProps: ["last_step", "step_index", "time_in_flow_ms"],
  },
  lead_search_performed: {
    featureArea: "leads",
    priority: "P1",
    allowedProps: ["search_type", "result_count", "action_taken"],
  },
  lead_opened: {
    featureArea: "leads",
    priority: "P0",
    allowedProps: ["opportunity_id", "from", "position"],
  },

  // Opportunity lifecycle
  opportunity_created: {
    featureArea: "leads",
    priority: "P0",
    allowedProps: [
      "created_via",
      "opportunity_type",
      "department_id",
      "lead_source",
    ],
  },
  opportunity_stage_changed: {
    featureArea: "leads",
    priority: "P0",
    allowedProps: [
      "opportunity_id",
      "opportunity_type",
      "from_stage",
      "to_stage",
    ],
  },
  opportunity_won: {
    featureArea: "leads",
    priority: "P0",
    allowedProps: ["opportunity_id", "opportunity_type", "days_to_close"],
  },
  opportunity_lost: {
    featureArea: "leads",
    priority: "P0",
    allowedProps: [
      "opportunity_id",
      "opportunity_type",
      "loss_reason",
      "days_to_close",
    ],
  },

  // Calling & disposition
  call_started: {
    featureArea: "calling",
    priority: "P0",
    allowedProps: ["opportunity_id", "direction"],
  },
  call_connected: {
    featureArea: "calling",
    priority: "P0",
    allowedProps: ["opportunity_id", "duration_sec", "attempt_number"],
  },
  disposition_form_opened: {
    featureArea: "calling",
    priority: "P1",
    allowedProps: ["opportunity_id", "step_index"],
  },
  disposition_connectivity_selected: {
    featureArea: "calling",
    priority: "P1",
    allowedProps: ["opportunity_id", "step_index", "connectivity_status"],
  },
  disposition_remark_selected: {
    featureArea: "calling",
    priority: "P1",
    allowedProps: ["opportunity_id", "step_index", "remark_id"],
  },
  followup_scheduled: {
    featureArea: "calling",
    priority: "P0",
    allowedProps: ["opportunity_id", "lead_time_hrs"],
  },
  lead_disposition_submitted: {
    featureArea: "calling",
    priority: "P0",
    allowedProps: [
      "opportunity_id",
      "connectivity_status",
      "previous_status",
      "new_status",
      "new_stage",
      "has_followup",
    ],
  },
  disposition_abandoned: {
    featureArea: "calling",
    priority: "P1",
    allowedProps: [
      "opportunity_id",
      "last_step",
      "step_index",
      "time_in_flow_ms",
    ],
  },

  // Patient-level disposition
  patient_rnr_submitted: {
    featureArea: "calling",
    priority: "P1",
    allowedProps: [
      "attempt_number",
      "auto_converted_to_dnd",
      "open_opps_affected",
    ],
  },
  patient_dnd_flagged: {
    featureArea: "calling",
    priority: "P1",
    allowedProps: ["flagged_opps"],
  },
  patient_cbl_submitted: {
    featureArea: "calling",
    priority: "P1",
    allowedProps: ["tasks_created", "open_opps_affected"],
  },

  // Booking wizards
  booking_started: {
    featureArea: "booking",
    priority: "P0",
    allowedProps: ["booking_id", "booking_type", "opportunity_id"],
  },
  booking_opportunity_selected: {
    featureArea: "booking",
    priority: "P0",
    allowedProps: [
      "booking_id",
      "booking_type",
      "step_index",
      "opportunity_type",
      "is_new_opportunity",
    ],
  },
  booking_doctor_selected: {
    featureArea: "booking",
    priority: "P0",
    allowedProps: [
      "booking_id",
      "booking_type",
      "step_index",
      "doctor_id",
      "speciality_id",
      "service_id",
    ],
  },
  booking_tests_selected: {
    featureArea: "booking",
    priority: "P0",
    allowedProps: [
      "booking_id",
      "booking_type",
      "step_index",
      "department_id",
      "test_count",
      "diagnostic_type",
    ],
  },
  booking_slot_selected: {
    featureArea: "booking",
    priority: "P0",
    allowedProps: [
      "booking_id",
      "booking_type",
      "step_index",
      "appointment_date",
    ],
  },
  booking_details_reviewed: {
    featureArea: "booking",
    priority: "P1",
    allowedProps: [
      "booking_id",
      "booking_type",
      "step_index",
      "amount",
      "currency",
    ],
  },
  booking_payment_initiated: {
    featureArea: "booking",
    priority: "P0",
    allowedProps: ["booking_id", "booking_type", "step_index", "payment_mode"],
  },
  appointment_booked: {
    featureArea: "booking",
    priority: "P0",
    allowedProps: [
      "booking_id",
      "booking_type",
      "step_index",
      "opportunity_id",
      "doctor_id",
      "department_id",
      "amount",
      "payment_mode",
      "test_count",
    ],
  },
  booking_abandoned: {
    featureArea: "booking",
    priority: "P0",
    allowedProps: [
      "booking_id",
      "booking_type",
      "last_step",
      "step_index",
      "time_in_flow_ms",
    ],
  },

  // Post-booking
  appointment_rescheduled: {
    featureArea: "booking",
    priority: "P1",
    allowedProps: ["booking_type", "reason", "days_before_appt"],
  },
  appointment_cancelled: {
    featureArea: "booking",
    priority: "P1",
    allowedProps: ["booking_type", "reason", "days_before_appt"],
  },

  // Wave 2 · My Leads working
  leads_tab_switched: {
    featureArea: "leads",
    priority: "P1",
    allowedProps: ["tab", "result_count"],
  },
  leads_filter_applied: {
    featureArea: "leads",
    priority: "P1",
    allowedProps: ["filter_key", "value_count", "active_filter_count"],
  },

  // Wave 2 · Patient 360
  patient_360_opened: {
    featureArea: "patient360",
    priority: "P1",
    allowedProps: ["entry_point", "opportunity_count", "open_task_count"],
  },
  patient_360_tab_viewed: {
    featureArea: "patient360",
    priority: "P1",
    allowedProps: ["tab"],
  },
  note_added: {
    featureArea: "patient360",
    priority: "P1",
    allowedProps: ["has_attachment"],
  },
  rx_viewed: {
    featureArea: "patient360",
    priority: "P1",
    allowedProps: ["opportunity_id"],
  },
  contact_channel_clicked: {
    featureArea: "patient360",
    priority: "P1",
    allowedProps: ["channel"],
  },

  // Wave 2 · Patient-registration funnel
  // featureArea "patient360" is DELIBERATE: PRD A.6 groups registration with
  // intake, but the UI surface lives inside Lead Details (patient 360).
  patient_registration_started: {
    featureArea: "patient360",
    priority: "P1",
    allowedProps: ["step_index"],
  },
  patient_registration_submitted: {
    featureArea: "patient360",
    priority: "P1",
    allowedProps: ["step_index"],
  },
  patient_registration_abandoned: {
    featureArea: "patient360",
    priority: "P1",
    allowedProps: ["last_step", "step_index", "time_in_flow_ms"],
  },

  // Wave 2 · Tasks
  task_opened: {
    featureArea: "tasks",
    priority: "P1",
    allowedProps: [
      "task_source",
      "task_type",
      "priority",
      "is_unread",
      "sla_breached",
    ],
  },
  task_completed: {
    featureArea: "tasks",
    priority: "P1",
    allowedProps: [
      "task_source",
      "task_type",
      "time_to_complete_ms",
      "disposition",
    ],
  },
  task_disposition_set: {
    featureArea: "tasks",
    priority: "P1",
    allowedProps: ["department_id", "disposition", "phase"],
  },
  task_reopened: {
    featureArea: "tasks",
    priority: "P1",
    allowedProps: ["task_source", "reopen_count"],
  },
  task_created: {
    featureArea: "tasks",
    priority: "P1",
    allowedProps: ["task_type", "has_due_date", "linked_to_opportunity"],
  },

  // Wave 2 · Overview workspace (+ bulk_update funnel)
  overview_tab_viewed: {
    featureArea: "overview",
    priority: "P1",
    allowedProps: ["tab", "result_count"],
  },
  overview_filter_applied: {
    featureArea: "overview",
    priority: "P1",
    allowedProps: ["tab", "filter_key", "value_count", "active_filter_count"],
  },
  overview_record_opened: {
    featureArea: "overview",
    priority: "P1",
    allowedProps: ["tab", "entity_type"],
  },
  bulk_update_started: {
    featureArea: "overview",
    priority: "P1",
    allowedProps: [
      "entity",
      "selection_count",
      "used_select_all",
      "step_index",
    ],
  },
  bulk_update_applied: {
    featureArea: "overview",
    priority: "P1",
    allowedProps: [
      "entity",
      "selection_count",
      "fields_changed",
      "affected_count",
      "result",
      "step_index",
    ],
  },
  bulk_update_abandoned: {
    featureArea: "overview",
    priority: "P2",
    allowedProps: ["entity", "last_step", "step_index", "time_in_flow_ms"],
  },

  // Wave 2 · Data exports
  export_started: {
    featureArea: "exports",
    priority: "P1",
    allowedProps: ["entity", "active_filter_count", "step_index"],
  },
  export_submitted: {
    featureArea: "exports",
    priority: "P1",
    allowedProps: ["entity", "export_id", "step_index"],
  },
  export_completed: {
    featureArea: "exports",
    priority: "P1",
    allowedProps: ["entity", "export_id", "status"],
  },
  export_abandoned: {
    featureArea: "exports",
    priority: "P2",
    allowedProps: ["entity", "last_step", "step_index", "time_in_flow_ms"],
  },

  // Wave 2 · Dashboards
  dashboard_viewed: {
    featureArea: "dashboards",
    priority: "P1",
    allowedProps: ["dashboard_type", "date_preset"],
  },
  agent_dashboard_viewed: {
    featureArea: "dashboards",
    priority: "P1",
    allowedProps: ["period_tab"],
  },

  // Wave 2 · Notifications
  notification_center_opened: {
    featureArea: "notifications",
    priority: "P1",
    allowedProps: ["unread_count"],
  },
  notification_clicked: {
    featureArea: "notifications",
    priority: "P1",
    allowedProps: ["entity_type", "priority", "time_to_click_ms"],
  },

  // Wave 2 · Broadcasts
  broadcast_sent: {
    featureArea: "broadcasts",
    priority: "P1",
    allowedProps: ["priority", "target_type", "recipient_count", "scheduled"],
  },

  // Wave 2 · Configuration & administration
  config_entity_changed: {
    featureArea: "config",
    priority: "P1",
    allowedProps: ["entity", "action"],
  },
  web_form_published: {
    featureArea: "config",
    priority: "P1",
    allowedProps: ["field_count", "facility_bound"],
  },

  // Reliability
  api_error_encountered: {
    featureArea: "reliability",
    priority: "P1",
    allowedProps: ["endpoint_group", "status_code", "method"],
  },
  socket_disconnected: {
    featureArea: "reliability",
    priority: "P1",
    allowedProps: ["reason", "reconnect_attempts", "session_age_ms"],
  },
};

export const EVENT_NAMES = Object.keys(EVENT_DEFS) as EventName[];
